import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Modal, Slider, Switch, Upload, message } from "antd";
import type { UploadProps } from "antd";
import {
  CheckCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  MinusOutlined,
  PlusOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { getPdfTemplateLabels, type TextSchema } from "../../i18n";
import {
  buildCompliancePdfBlob,
  defaultCompliancePdfFileName,
} from "../../utils/exportCompliancePdf";
import { PdfTemplate, type PdfReportInput } from "../PdfTemplate";
import "./export-pdf.css";

type PdfCore = Omit<PdfReportInput, "includeCover" | "logoDataUrl" | "labels">;

export type ExportPdfProps = PdfCore & {
  open: boolean;
  onClose: () => void;
  t: TextSchema;
};

const PREVIEW_SCALE_MIN = 0.35;
const PREVIEW_SCALE_MAX = 0.95;
const PREVIEW_SCALE_DEFAULT = 0.55;
const PREVIEW_SCALE_STEP = 0.05;

export function ExportPdf({ open, onClose, t, ...report }: ExportPdfProps) {
  const [includeCover, setIncludeCover] = useState(true);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState<string | null>(null);
  const [previewScale, setPreviewScale] = useState(PREVIEW_SCALE_DEFAULT);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    if (open) {
      setIncludeCover(true);
      setLogoDataUrl(null);
      setLogoFileName(null);
      setPreviewScale(PREVIEW_SCALE_DEFAULT);
    }
  }, [open]);

  const pdfLabels = getPdfTemplateLabels(t);

  const templateProps: PdfReportInput = {
    ...report,
    includeCover,
    logoDataUrl,
    labels: pdfLabels,
  };

  const handleDownloadPdf = useCallback(async () => {
    setPdfGenerating(true);
    try {
      const blob = await buildCompliancePdfBlob();
      const name = defaultCompliancePdfFileName(report.scanIdShort);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      void message.success(t.studioExportPdfDownloadOk);
    } catch (err) {
      console.error(err);
      void message.error(t.studioExportPdfDownloadFailed);
    } finally {
      setPdfGenerating(false);
    }
  }, [report.scanIdShort, t]);

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    const isImg =
      file.type === "image/png" || file.type === "image/jpeg" || file.type === "image/jpg";
    if (!isImg) {
      void message.error(t.studioExportPdfLogoInvalidType);
      return Upload.LIST_IGNORE;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setLogoDataUrl(reader.result);
        setLogoFileName(file.name);
        void message.success(t.studioExportPdfLogoUploadOk);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const zoomOut = () =>
    setPreviewScale((s) => Math.max(PREVIEW_SCALE_MIN, s - PREVIEW_SCALE_STEP));
  const zoomIn = () =>
    setPreviewScale((s) => Math.min(PREVIEW_SCALE_MAX, s + PREVIEW_SCALE_STEP));
  const zoomReset = () => setPreviewScale(PREVIEW_SCALE_DEFAULT);

  const printMount =
    open &&
    createPortal(
      <div className="pdf-print-mount" aria-hidden>
        <PdfTemplate {...templateProps} />
      </div>,
      document.body,
    );

  const zoomPercent = Math.round(previewScale * 100);

  return (
    <>
      {printMount}
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={960}
        centered
        destroyOnClose
        className="export-pdf-modal"
        closable={false}
      >
        <div className="export-pdf-shell">
          <div className="export-pdf-preview-col">
            <div className="export-pdf-zoom-bar">
              <span className="export-pdf-zoom-label">{t.studioExportPdfZoom}</span>
              <div className="export-pdf-zoom-controls">
                <Button
                  type="text"
                  size="small"
                  icon={<MinusOutlined />}
                  onClick={zoomOut}
                  aria-label={t.studioExportPdfZoomOut}
                />
                <Slider
                  className="export-pdf-zoom-slider"
                  min={PREVIEW_SCALE_MIN}
                  max={PREVIEW_SCALE_MAX}
                  step={PREVIEW_SCALE_STEP}
                  value={previewScale}
                  onChange={setPreviewScale}
                  tooltip={{ formatter: (v) => (v != null ? `${Math.round(v * 100)}%` : "") }}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={zoomIn}
                  aria-label={t.studioExportPdfZoomIn}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<UndoOutlined />}
                  onClick={zoomReset}
                  aria-label={t.studioExportPdfZoomReset}
                />
              </div>
              <span className="export-pdf-zoom-pct">{zoomPercent}%</span>
            </div>
            <div className="export-pdf-preview-scroll">
              <div
                className="export-pdf-preview-scale"
                style={{
                  transform: `scale(${previewScale})`,
                }}
              >
                <PdfTemplate {...templateProps} compact />
              </div>
            </div>
            <p className="export-pdf-preview-label">{t.studioExportPdfPreview}</p>
          </div>
          <div className="export-pdf-options-col">
            <div className="export-pdf-options-head">
              <h2 className="export-pdf-options-title">{t.studioExportPdfTitle}</h2>
              <button
                type="button"
                className="export-pdf-close"
                onClick={onClose}
                aria-label={t.studioExportPdfCancel}
              >
                <CloseOutlined />
              </button>
            </div>

            <div className="export-pdf-toggle-row" style={{ marginBottom: 24 }}>
              <div>
                <p className="export-pdf-field-label">{t.studioExportPdfCoverLine1}</p>
                {t.studioExportPdfCoverLine2.trim() ? (
                  <p className="export-pdf-field-hint" style={{ marginBottom: 0 }}>
                    {t.studioExportPdfCoverLine2}
                  </p>
                ) : null}
              </div>
              <Switch checked={includeCover} onChange={setIncludeCover} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <p className="export-pdf-field-label">{t.studioExportPdfLogoLine1}</p>
              {!logoDataUrl ? (
                <>
                  {t.studioExportPdfLogoLine2.trim() ? (
                    <p className="export-pdf-field-hint">{t.studioExportPdfLogoLine2}</p>
                  ) : null}
                  <Upload.Dragger
                    name="logo"
                    multiple={false}
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    accept="image/png,image/jpeg"
                    className="export-pdf-dragger"
                  >
                    <div className="export-pdf-upload">
                      <CloudUploadOutlined className="export-pdf-upload-icon" />
                      <p style={{ margin: "8px 0 0", fontSize: 12, color: "#4f5f7b" }}>
                        {t.studioExportPdfUploadHint}
                      </p>
                    </div>
                  </Upload.Dragger>
                </>
              ) : (
                <div className="export-pdf-logo-feedback">
                  <CheckCircleOutlined className="export-pdf-logo-feedback-icon" aria-hidden />
                  <div className="export-pdf-logo-feedback-text">
                    <span className="export-pdf-logo-feedback-name" title={logoFileName ?? ""}>
                      {logoFileName ?? "—"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="export-pdf-logo-feedback-remove"
                    onClick={() => {
                      setLogoDataUrl(null);
                      setLogoFileName(null);
                    }}
                  >
                    {t.studioExportPdfLogoRemove}
                  </button>
                </div>
              )}
            </div>

            <div className="export-pdf-disclaimer">
              <p>{t.studioExportPdfDisclaimer}</p>
            </div>

            <div className="export-pdf-footer">
              <button
                type="button"
                className="export-pdf-btn-primary"
                disabled={pdfGenerating}
                onClick={() => void handleDownloadPdf()}
              >
                {pdfGenerating ? t.studioExportPdfGenerating : t.studioExportPdfGenerate}
              </button>
              <button type="button" className="export-pdf-btn-ghost" onClick={onClose}>
                {t.studioExportPdfCancel}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
